import { isIsoDate } from '../utils/liveEmbedDate.mjs';
import { sanitizeLiveEmbedPreferences } from '../utils/liveEmbedCompetitions.mjs';

export const LIVE_EMBED_PARENT_ORIGIN = 'https://app.dale365.com';

const MESSAGE_TYPES = Object.freeze({
  ready: 'dale365-live-embed-ready',
  context: 'dale365-live-embed-context',
  preferences: 'dale365-live-embed-preferences',
  date: 'dale365-live-embed-date',
});

export function isTrustedParentMessage(event, parentWindow, trustedOrigin = LIVE_EMBED_PARENT_ORIGIN) {
  return !!event
    && !!parentWindow
    && event.source === parentWindow
    && event.origin === trustedOrigin;
}

function validRevision(value) {
  return Number.isSafeInteger(value) && value >= 0;
}

function validDatePayload(data) {
  return isIsoDate(data?.date) && typeof data?.isCurrent === 'boolean';
}

export function createLiveEmbedBridge({
  parentWindow,
  targetOrigin = LIVE_EMBED_PARENT_ORIGIN,
  onContext = () => {},
  onDate = () => {},
  onPreferencesUnconfirmed = () => {},
  preferenceAckTimeoutMs = 1500,
  schedule = globalThis.setTimeout,
  cancelSchedule = globalThis.clearTimeout,
} = {}) {
  let appliedRevision = -1;
  let pendingRevision = -1;
  let pendingValue = null;
  let acknowledgementTimer = null;

  function clearAcknowledgementTimer() {
    if (acknowledgementTimer === null) return;
    cancelSchedule(acknowledgementTimer);
    acknowledgementTimer = null;
  }

  function scheduleUnconfirmedRecovery(revision) {
    clearAcknowledgementTimer();
    acknowledgementTimer = schedule(() => {
      acknowledgementTimer = null;
      if (pendingRevision !== revision || !pendingValue) return;
      onPreferencesUnconfirmed({
        ...pendingValue,
        competitionIds: [...pendingValue.competitionIds],
        gameTypes: [...pendingValue.gameTypes],
      });
    }, preferenceAckTimeoutMs);
  }

  function post(message) {
    if (!parentWindow || typeof parentWindow.postMessage !== 'function') return false;
    parentWindow.postMessage(message, targetOrigin);
    return true;
  }

  return {
    sendReady(view) {
      return post({ type: MESSAGE_TYPES.ready, version: 1, view: String(view || 'all') });
    },
    sendPreferences(preferences, revision) {
      const validPreferences = sanitizeLiveEmbedPreferences(preferences);
      if (!validPreferences || (revision !== undefined && !validRevision(revision))) return false;
      const nextRevision = Math.max(
        appliedRevision + 1,
        pendingRevision + 1,
        revision ?? 0,
      );
      const sent = post({
        type: MESSAGE_TYPES.preferences,
        version: 1,
        revision: nextRevision,
        preferences: validPreferences,
      });
      if (!sent) return false;
      pendingRevision = nextRevision;
      pendingValue = validPreferences;
      scheduleUnconfirmedRecovery(nextRevision);
      return nextRevision;
    },
    sendDate(date, isCurrent) {
      if (!isIsoDate(date) || typeof isCurrent !== 'boolean') return false;
      return post({ type: MESSAGE_TYPES.date, version: 1, date, isCurrent });
    },
    handle(event) {
      if (!isTrustedParentMessage(event, parentWindow, targetOrigin)) return false;
      const data = event?.data;
      if (!data || typeof data !== 'object' || data.version !== 1) return false;

      if (data.type === MESSAGE_TYPES.context) {
        const preferences = sanitizeLiveEmbedPreferences(data.preferences);
        if (!preferences || !validRevision(data.revision) || data.revision < appliedRevision) return false;
        if (data.date !== undefined && !isIsoDate(data.date)) return false;
        const preferencesChanged = data.revision > appliedRevision;
        if (preferencesChanged) {
          appliedRevision = data.revision;
          onContext({ revision: data.revision, preferences });
        }
        if (data.revision >= pendingRevision) {
          clearAcknowledgementTimer();
          pendingRevision = data.revision;
          pendingValue = null;
        }
        if (data.date !== undefined) {
          onDate({
            date: data.date,
            isCurrent: typeof data.isCurrent === 'boolean' ? data.isCurrent : undefined,
          });
        }
        return preferencesChanged || data.date !== undefined;
      }

      if (data.type === MESSAGE_TYPES.date && validDatePayload(data)) {
        onDate({ date: data.date, isCurrent: data.isCurrent });
        return true;
      }
      return false;
    },
    revision() {
      return appliedRevision;
    },
    pendingPreferences() {
      return pendingValue ? {
        ...pendingValue,
        competitionIds: [...pendingValue.competitionIds],
        gameTypes: [...pendingValue.gameTypes],
      } : null;
    },
    stop() {
      clearAcknowledgementTimer();
    },
  };
}
